import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Pill from "../../../../../components/atoms/Pill";
import Expander from "../../../../../components/Expander";
import TextExpander from "../../../../../components/TextExpander";
import PublicationLinks from "../../../../../components/Publications/components/PublicationLinks";
import { getVarsoUrl } from "../../../../../common/helpers/helpers";
import withErrorGuard from "../../../../../HOC/withErrorGuard";
import RenderBullet from "../../../../../components/atoms/RenderBullet/RenderBullet";
import ComponentPanel from "../../../../../components/atoms/ComponentPanel/ComponentPanel";
import {
  blackColor,
  darkCharcoal,
  gravelFint,
} from "../../../../../common/helpers/colors";
import HelperText from "../../../../../components/atoms/HelperText";

const { arrayOf, shape, number, string, node, bool } = PropTypes;

const TIER_TO_PATHOGENICITY = {
  1: "P",
  2: "LP",
  3: "VUS",
  4: "LB",
};

const getTierColor = (givenTier, variantClasses) => {
  const tier = TIER_TO_PATHOGENICITY[givenTier];

  // If uknown tier color black
  if (tier && variantClasses[tier]) {
    return variantClasses[tier].color;
  }
  return blackColor;
};

class PMKBInternal extends React.Component {
  render() {
    let tmpl;
    const { result, isGene, variantClasses } = this.props;
    let version;
    if (result) {
      const resultArray = !Array.isArray(result) ? [result] : result;
      tmpl = Array.isArray(resultArray)
        ? resultArray.map((item, i) => {
            version = item.version;
            return (
              <div className="varsome-inner-container" key={i}>
                <table className="varsome-table">
                  <thead>
                    <tr>
                      <th width="5%">Tier</th>
                      <th width="20%">Tumor</th>
                      <th width="8%">Tissue</th>
                      <th width="12%">Definition</th>
                      <th width="50%">Interpretation</th>
                      <th width="25%">PUBMED references</th>
                    </tr>
                  </thead>
                  <Expander showMore="5" component="tbody">
                    {item.items.map((mappedItem, j) => {
                      let definitions;
                      if (
                        mappedItem.variants &&
                        Array.isArray(mappedItem.variants)
                      ) {
                        definitions = mappedItem.variants.map(
                          (d) => d.definition
                        );
                      }
                      if (!definitions) {
                        definitions = mappedItem.definition;
                      }
                      return (
                        <tr key={`${i}-${j}`}>
                          <td>
                            <Pill
                              className="tw-text-base tw-p-2"
                              color={getTierColor(
                                mappedItem.tier,
                                variantClasses
                              )}
                            >
                              {mappedItem.tier}
                            </Pill>
                          </td>
                          <td>
                            {Array.isArray(mappedItem.tumour_types) ? (
                              <Expander showMore="5">
                                {mappedItem.tumour_types.map((t, k) => (
                                  <div key={`${k}-${i}-${j}`}>
                                    <RenderBullet />
                                    {t}
                                  </div>
                                ))}
                              </Expander>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {Array.isArray(mappedItem.tissues) ? (
                              <Expander showMore="5">
                                {mappedItem.tissues.map((t, c) => (
                                  <div key={`bullet-${i}-${c}`}>
                                    <RenderBullet />
                                    {t}
                                  </div>
                                ))}
                              </Expander>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {Array.isArray(definitions) &&
                              definitions.map((def, index) => (
                                <div
                                  key={index}
                                  style={{
                                    color:
                                      def &&
                                      (def.includes("copy number loss") ||
                                        def.includes("rearrangement") ||
                                        def.includes("copy number gain"))
                                        ? gravelFint
                                        : darkCharcoal,
                                  }}
                                >
                                  <RenderBullet />
                                  {def}
                                </div>
                              ))}
                          </td>
                          <td>
                            <TextExpander
                              text={mappedItem.interpretations}
                              truncate="300"
                            />
                          </td>
                          <td>
                            {mappedItem.pub_med_references ? (
                              <PublicationLinks
                                ids={mappedItem.pub_med_references}
                              />
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Expander>
                </table>
              </div>
            );
          })
        : null;
      const res = isGene ? (
        <div className="varsome-result-fixed">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              title="PMKB"
              version={version}
              url={getVarsoUrl(
                "https://eipm.weill.cornell.edu/resources/precision-medicine-knowledgebase/"
              )}
              helperPopup={<HelperText fieldDescriptionKey="PMKB" />}
              content={tmpl}
            />
          </div>
        </div>
      ) : (
        <div className="varsome-result-fixed">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              title="PMKB"
              version={version}
              url={getVarsoUrl(
                "https://eipm.weill.cornell.edu/resources/precision-medicine-knowledgebase/"
              )}
              helperPopup={<HelperText fieldDescriptionKey="PMKB" />}
              content={tmpl}
            />
          </div>
        </div>
      );
      return res;
    }
    return null;
  }
}

PMKBInternal.propTypes = {
  result: shape({
    version: string,
    items: arrayOf(
      shape({
        tier: number,
        definition: arrayOf(string),
        interpretations: string,
        tissues: arrayOf(string),
        tumour_types: arrayOf(string),
        disease_or_trait: node,
        pub_med_references: arrayOf(number),
        variants: arrayOf(
          shape({
            variants: arrayOf(string),
            type: string,
            definition: string,
            gene_id: number,
          })
        ),
      })
    ),
  }),
  isGene: bool,
  variantClasses: shape({}),
};

PMKBInternal.defaultProps = {
  result: null,
  isGene: false,
  variantClasses: null,
};

const mapStateToProps = (state) => ({
  variantClasses: state?.variantClasses,
});

export default connect(mapStateToProps)(
  withErrorGuard(PMKBInternal, "PMKB", "PMKB")
);
